import React, { createContext, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { COOKIES } from '../services/exports/Constants';
import useAppCookies from '../hooks/utility/useAppCookies';
import WebViewCommunicator from '../services/helpers/WebViewCommunicator';
import ReduxHooks from '../store/ReduxHooks';
import { actionCreators } from '../store/actions';
import currentFranchiseSlice from '../store/slices/CurrentFranchiseSlice';
import Analytics from '../services/analytics/Analytics';
import { syncPartnerSlugForLogout } from '../services/branding/partnerSlugStorage';

export const AuthContext = createContext(null);

export default function AuthProvider({ children }) {
  const { getCookie, setCookie, removeCookies } = useAppCookies([COOKIES.AUTH_TOKEN]);

  const authToken = getCookie(COOKIES.AUTH_TOKEN);
  const onBehalfOf = getCookie(COOKIES.ON_BEHALF_OF_STORE);

  const isLoggedIn = !!getCookie(COOKIES.AUTH_TOKEN);

  useEffect(() => {
    extendCookiesExpiration();
  }, []);

  function resetSessionState() {
    ReduxHooks.dispatch(actionCreators.initialData.reset());
    ReduxHooks.dispatch(actionCreators.currentServiceProvider.reset());
    ReduxHooks.dispatch(actionCreators.currentCompany.reset());
    ReduxHooks.dispatch(currentFranchiseSlice.actions.reset());
  }

  function extendCookiesExpiration() {
    authToken &&
      setCookie(COOKIES.AUTH_TOKEN, authToken, {
        expires: moment().add(399, 'days').toDate(),
      });

    onBehalfOf &&
      setCookie(COOKIES.ON_BEHALF_OF_STORE, onBehalfOf, {
        expires: moment().add(399, 'days').toDate(),
      });
  }

  const context = useMemo(
    () => ({
      isLoggedIn,
      logIn: async (data) => {
        try {
          // The persisted tenant context belongs to the PREVIOUS session —
          // e.g. a super admin's "FoodAmigos" provider must never carry over
          // into a partner admin's session.
          resetSessionState();
          setCookie(COOKIES.AUTH_TOKEN, data.data.access_token, {
            expires: moment().add(399, 'days').toDate(),
          });
          WebViewCommunicator.forwardAuthToken(data.data.access_token);
        } catch (error) {
          console.error('Failed to log the user in: ', error);
        }
      },
      logOut: () => {
        try {
          // Remember the session's partner so the login page stays branded
          // after the tenant state below is wiped.
          syncPartnerSlugForLogout(ReduxHooks.getStore().currentServiceProvider?.serviceProvider);
          removeCookies([COOKIES.AUTH_TOKEN, COOKIES.ON_BEHALF_OF_STORE]);
          resetSessionState();
          WebViewCommunicator.forwardAuthToken(null);
          Analytics.reset();
        } catch (error) {
          console.error('Failed to log the user out: ', error);
        }
      },
    }),
    [isLoggedIn],
  );

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
}
